import { useTranslations } from '@/i18n/use-translations';
import { inviteToOrganizationSchemaFactory } from '@/schemas/organizations/invite-to-organization';
import { useInviteToOrganizationMutation } from '@/state/mutations/invitations/use-invite-to-organization.ts';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useUser } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, TextField, Typography } from '@mui/material';
import { useNotifications } from '@/hooks/use-notifications';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ORGANIZATION_ROLES, type OrganizationRole } from '@/enums/organization-roles.enum.ts';
import { organizationRoleLabel } from '@/components/organizations/organization-invitation-role-labels';
import {
  normalizeOrgElevatedDraft,
  orgElevatedCategoryOf,
} from '@/components/organizations/org-elevated-role-categories';

export default function InviteForm({ onSuccess }: { onCancel: () => void; onSuccess: () => void }) {
  const notification = useNotifications();
  const { user } = useUser();
  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);
  const t = useTranslations();

  const inviteToOrganizationsSchema = inviteToOrganizationSchemaFactory(t);
  type InviteToOrganizationFormType = z.infer<typeof inviteToOrganizationsSchema>;
  const { control, handleSubmit } = useForm<InviteToOrganizationFormType>({
    resolver: zodResolver(inviteToOrganizationsSchema),
    defaultValues: {
      name: '',
      email: '',
      message: '',
      elevatedRoles: [],
    },
  });

  const { mutate, isSuccess, isPending, error } = useInviteToOrganizationMutation();

  useEffect(() => {
    if (isSuccess) {
      onSuccess();
      notification.notifySuccess(t('organization.invite.success'));
    }
  }, [isSuccess]);
  useEffect(() => {
    if (error) {
      notification.notifyError(t('organization.invite.error'));
    }
  }, [error]);

  const onSubmit = (data: InviteToOrganizationFormType) => {
    const payload = {
      ...data,
      // without active user we would not be inside app
      userId: user!.id,
      // without active organization we could not click on button opening this form
      organizationId: activeOrganization!.id,
    };
    mutate(payload);
  };

  return (
    <Box
      noValidate
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        mt: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            required
            fullWidth
            type="text"
            variant="standard"
            label={t('organization.invite.name')}
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            required
            fullWidth
            type="email"
            variant="standard"
            label={t('organization.invite.email')}
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
            sx={{ mt: 2 }}
          />
        )}
      />
      <Controller
        name="message"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            multiline
            variant="standard"
            label={t('organization.invite.message')}
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
            sx={{ mt: 2 }}
          />
        )}
      />

      <Controller
        name="elevatedRoles"
        control={control}
        render={({ field }) => (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">{t('organization.invite.elevatedRoles')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('organization.invite.elevatedRolesHint')}
            </Typography>
            <FormGroup>
              {(
                [
                  ORGANIZATION_ROLES.OWNER,
                  ORGANIZATION_ROLES.ADMIN,
                  ORGANIZATION_ROLES.EVENT_PLANNER,
                  ORGANIZATION_ROLES.EVENT_ORGANIZER,
                  ORGANIZATION_ROLES.CONTENT_MANAGER,
                ] as const
              ).map((role) => (
                <FormControlLabel
                  key={role}
                  control={
                    <Checkbox
                      checked={field.value.includes(role)}
                      onChange={(_, checked) => {
                        const cur = field.value as OrganizationRole[];
                        if (checked) {
                          const cat = orgElevatedCategoryOf(role);
                          const withoutSameCat = cat ? cur.filter((r) => orgElevatedCategoryOf(r) !== cat) : cur;
                          field.onChange(normalizeOrgElevatedDraft([...withoutSameCat, role]));
                        } else {
                          field.onChange(cur.filter((r: OrganizationRole) => r !== role));
                        }
                      }}
                      disabled={isPending}
                    />
                  }
                  label={organizationRoleLabel(role, t)}
                />
              ))}
            </FormGroup>
          </Box>
        )}
      />

      <Box sx={{ display: 'flex', mt: 2, gap: 2, width: '100%', justifyContent: 'end' }}>
        <Button type="submit" variant="contained" size="small" loading={isPending}>
          {t('common.create')}
        </Button>
      </Box>
    </Box>
  );
}
